import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { FileText, Minus, Plus, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Button } from '@/components/Button/Button';
import { useDialogFocusReturn } from '@/hooks/useDialogFocusReturn';

import type { UserRole } from './useCourse';
import {
  useCreateCourseComponent,
  type CreateCourseComponentKind,
} from './useCreateCourseComponent';
import styles from './CreateSectionFAB.module.css';

interface CreateSectionFABProps {
  courseId?: string;
  isEditing: boolean;
  userRole: UserRole;
}

interface CreateDialogProps {
  errorMessage: string | null;
  isPending: boolean;
  kind: CreateCourseComponentKind;
  onOpenChange(open: boolean): void;
  onSubmit(title: string): Promise<void>;
  open: boolean;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Unable to create this item.';
}

function getDialogCopy(kind: CreateCourseComponentKind) {
  if (kind === 'divider') {
    return {
      actionLabel: 'Save',
      description:
        'Create a non-clickable category divider for organizing the course list.',
      fieldLabel: 'Category title',
      title: 'New Section Divider',
    };
  }
  if (kind === 'kai_coach') {
    return {
      actionLabel: 'Create',
      description:
        'Create an AI coaching module and open its editor immediately. You can author Kai’s standing instructions and sessions there.',
      fieldLabel: 'Kai Coach title',
      title: 'New Kai Coach',
    };
  }
  return {
    actionLabel: 'Create',
    description:
      'Create a content section and open it in the editor immediately.',
    fieldLabel: 'Section title',
    title: 'New Section',
  };
}

function CreateDialog({
  errorMessage,
  isPending,
  kind,
  onOpenChange,
  onSubmit,
  open,
}: CreateDialogProps) {
  const copy = getDialogCopy(kind);
  const inputId = useId();
  const errorId = useId();
  const [localError, setLocalError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const trimmedTitle = title.trim();
  const activeError = localError ?? errorMessage;
  // Not autoFocus: that moves focus before Radix keeps it in the dialog (#173).
  const titleRef = useRef<HTMLInputElement>(null);
  const focusReturn = useDialogFocusReturn({ initialFocus: titleRef });

  useEffect(() => {
    if (open) {
      setLocalError(null);
      return;
    }

    setTitle('');
    setLocalError(null);
  }, [open]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!trimmedTitle) {
      setLocalError('Enter a title before creating this item.');
      return;
    }

    setLocalError(null);
    await onSubmit(trimmedTitle);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.dialog} {...focusReturn}>
          <Dialog.Title className={styles.dialogTitle}>{copy.title}</Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.dialogDescription}>{copy.description}</p>
          </Dialog.Description>

          <form className={styles.form} onSubmit={handleSubmit}>
            <label className={styles.field} htmlFor={inputId}>
              <span>{copy.fieldLabel}</span>
              <input
                aria-describedby={activeError ? errorId : undefined}
                autoComplete="off"
                className={styles.input}
                disabled={isPending}
                id={inputId}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setLocalError(null);
                }}
                ref={titleRef}
                value={title}
              />
            </label>

            {kind === 'divider' ? (
              <p className={styles.tip}>
                Tip: You can reorder dividers by enabling edit mode on the course
                home.
              </p>
            ) : null}

            {activeError ? (
              <p
                aria-live="polite"
                className={styles.error}
                id={errorId}
                role="alert"
              >
                {activeError}
              </p>
            ) : null}

            <div className={styles.dialogFooter}>
              <Dialog.Close asChild>
                <Button disabled={isPending} variant="secondary">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button disabled={!trimmedTitle || isPending} type="submit" variant="primary">
                {isPending ? 'Creating...' : copy.actionLabel}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function CreateSectionFAB({
  courseId,
  isEditing,
  userRole,
}: CreateSectionFABProps) {
  const createComponent = useCreateCourseComponent(courseId);
  const navigate = useNavigate();
  const [dialogKind, setDialogKind] = useState<CreateCourseComponentKind | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // An item opens its dialog once the menu has closed and given focus back to
  // Create New: opened at once, the menu's late focus return pulled focus out
  // of the dialog (#173), and the dialog takes Create New as its opener.
  const chosenKindRef = useRef<CreateCourseComponentKind | null>(null);

  if (userRole !== 'instructor' || isEditing || !courseId) {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setDialogKind(null);
      setErrorMessage(null);
    }
  };

  const handleSubmit = async (title: string) => {
    if (!dialogKind) {
      return;
    }

    setErrorMessage(null);

    try {
      const result = await createComponent.mutateAsync({
        kind: dialogKind,
        title,
      });

      setDialogKind(null);

      if (
        (dialogKind === 'section' || dialogKind === 'kai_coach') &&
        result.componentId
      ) {
        navigate(`/app/courses/${courseId}/${result.componentId}?edit=1`);
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <div className={styles.root}>
      <DropdownMenu.Root modal={false} onOpenChange={setIsMenuOpen} open={isMenuOpen}>
        {/* No click handler of its own: Radix opens the menu on the press
            and closes it on a second one, which a handler opening it on the
            click would undo (review of PR 116). */}
        <DropdownMenu.Trigger asChild>
          <button
            aria-label="Create new section or divider"
            className={styles.fabButton}
            type="button"
          >
            <Plus aria-hidden="true" size={24} strokeWidth={2.4} />
          </button>
        </DropdownMenu.Trigger>
        <span aria-hidden="true" className={styles.fabLabel}>
          Create New
        </span>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className={styles.menuContent}
            onCloseAutoFocus={() => {
              const kind = chosenKindRef.current;
              if (kind) {
                chosenKindRef.current = null;
                setDialogKind(kind);
              }
            }}
            side="top"
            sideOffset={12}
          >
            <DropdownMenu.Item
              aria-label="New Section/Chapter"
              className={styles.menuItem}
              onSelect={() => {
                chosenKindRef.current = 'section';
                setIsMenuOpen(false);
              }}
            >
              <FileText aria-hidden="true" size={16} />
              <span className={styles.menuItemText}>
                <span className={styles.menuItemLabel}>New Section/Chapter</span>
                <span className={styles.menuItemDescription}>
                  Add your own custom content
                </span>
              </span>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              aria-label="Kai Coach Section"
              className={styles.menuItem}
              onSelect={() => {
                chosenKindRef.current = 'kai_coach';
                setIsMenuOpen(false);
              }}
            >
              <Sparkles aria-hidden="true" size={16} />
              <span className={styles.menuItemText}>
                <span className={styles.menuItemLabel}>Kai Coach Section</span>
                <span className={styles.menuItemDescription}>
                  Add an AI coaching conversation
                </span>
              </span>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              aria-label="New Divider/Category"
              className={styles.menuItem}
              onSelect={() => {
                chosenKindRef.current = 'divider';
                setIsMenuOpen(false);
              }}
            >
              <Minus aria-hidden="true" size={16} />
              <span className={styles.menuItemText}>
                <span className={styles.menuItemLabel}>New Divider/Category</span>
                <span className={styles.menuItemDescription}>
                  Organize your content into groups
                </span>
              </span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <CreateDialog
        errorMessage={errorMessage}
        isPending={createComponent.isPending}
        kind={dialogKind ?? 'section'}
        onOpenChange={handleOpenChange}
        onSubmit={handleSubmit}
        open={dialogKind !== null}
      />
    </div>
  );
}
